class RovynelHeader extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-menu-toggle]');
    this.menu = this.querySelector('[data-mobile-menu]');
    if (!this.button || !this.menu) return;
    this.button.addEventListener('click', () => {
      const expanded = this.button.getAttribute('aria-expanded') === 'true';
      const nextExpanded = !expanded;
      this.button.setAttribute('aria-expanded', String(nextExpanded));
      this.button.setAttribute('aria-label', nextExpanded ? this.button.dataset.closeLabel : this.button.dataset.openLabel);
      this.menu.hidden = expanded;
    });
  }
}

class RovynelProduct extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-variant-select]');
    this.price = this.querySelector('[data-product-price]');
    this.button = this.querySelector('[data-add-to-cart]');
    this.initVariantSelector();
    this.initQuantitySelector();
    this.initGallery();
    this.initProductDetails();
  }

  initVariantSelector() {
    if (!this.select) return;
    this.select.addEventListener('change', () => {
      const option = this.select.options[this.select.selectedIndex];
      if (this.price && option.dataset.price) this.price.textContent = option.dataset.price;
      if (!this.button) return;
      const available = option.dataset.available === 'true';
      this.button.disabled = !available;
      this.button.textContent = available ? this.button.dataset.addLabel : this.button.dataset.soldoutLabel;
      if (option.dataset.imageId && this.galleryThumbnails) {
        const imageIndex = this.galleryThumbnails.findIndex((thumbnail) => thumbnail.dataset.imageId === option.dataset.imageId);
        if (imageIndex >= 0) this.selectGalleryImage(imageIndex);
      }
    });
  }

  initQuantitySelector() {
    const input = this.querySelector('[data-quantity-input]');
    if (!input) return;
    this.querySelectorAll('[data-quantity-change]').forEach((control) => {
      control.addEventListener('click', () => {
        const minimum = Number.parseInt(input.min, 10) || 1;
        const current = Number.parseInt(input.value, 10) || minimum;
        const change = Number.parseInt(control.dataset.quantityChange, 10) || 0;
        input.value = Math.max(minimum, current + change);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  initGallery() {
    this.gallery = this.querySelector('[data-product-gallery]');
    this.galleryMainImage = this.querySelector('.rv-product__main-image img');
    this.galleryMainButton = this.querySelector('[data-product-zoom-open]');
    this.galleryRail = this.querySelector('[data-product-thumbnails]');
    this.galleryThumbnails = Array.from(this.querySelectorAll('[data-product-thumbnail]'));
    this.galleryStatus = this.querySelector('[data-gallery-status]');
    this.galleryCurrent = this.querySelector('[data-product-image-current]');
    this.galleryPrevious = this.querySelector('[data-gallery-previous]');
    this.galleryNext = this.querySelector('[data-gallery-next]');
    this.galleryDialog = this.querySelector('[data-product-zoom-dialog]');
    this.galleryZoomImage = this.galleryDialog?.querySelector('img');
    this.galleryZoomCurrent = this.querySelector('[data-product-zoom-current]');
    this.currentGalleryIndex = Number.parseInt(this.gallery?.dataset.currentIndex, 10) || 0;
    this.galleryTargetIndex = this.currentGalleryIndex;
    this.galleryRequestId = 0;
    this.galleryImageCache = new Map();
    if (!this.gallery || !this.galleryMainImage) return;
    this.galleryMainButton?.addEventListener('click', (event) => {
      if (this.galleryDidSwipe) {
        event.preventDefault();
        this.galleryDidSwipe = false;
        return;
      }
      this.openGalleryDialog();
    });
    this.querySelector('[data-product-zoom-close]')?.addEventListener('click', () => this.galleryDialog?.close());
    this.galleryDialog?.addEventListener('click', (event) => {
      if (event.target === this.galleryDialog) this.galleryDialog.close();
    });
    this.galleryMainButton?.addEventListener('pointerdown', (event) => {
      this.galleryPointerStart = { x: event.clientX, y: event.clientY };
    });
    this.galleryMainButton?.addEventListener('pointerup', (event) => this.handleGallerySwipe(event));
    if (!this.galleryThumbnails.length) return;

    this.galleryThumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => this.selectGalleryImage(index));
      thumbnail.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        this.selectGalleryImage(this.galleryTargetIndex + direction, { focusThumbnail: true });
      });
    });

    this.galleryPrevious?.addEventListener('click', () => this.selectGalleryImage(this.galleryTargetIndex - 1));
    this.galleryNext?.addEventListener('click', () => this.selectGalleryImage(this.galleryTargetIndex + 1));
    this.updateGalleryControls();
    const scheduleInitialPreload = () => this.preloadAdjacentImages(this.currentGalleryIndex);
    if (this.galleryMainImage.complete) scheduleInitialPreload();
    else this.galleryMainImage.addEventListener('load', scheduleInitialPreload, { once: true });
  }

  selectGalleryImage(index, options = {}) {
    if (!this.galleryThumbnails?.length) return;
    const targetIndex = Math.max(0, Math.min(index, this.galleryThumbnails.length - 1));
    const target = this.galleryThumbnails[targetIndex];
    if (!target || targetIndex === this.galleryTargetIndex) return;
    if (targetIndex === this.currentGalleryIndex && this.galleryMainImage.complete) {
      this.galleryRequestId += 1;
      this.galleryTargetIndex = targetIndex;
      this.setActiveGalleryThumbnail(targetIndex, options.focusThumbnail);
      this.gallery.classList.remove('is-loading');
      this.gallery.setAttribute('aria-busy', 'false');
      if (this.galleryStatus) this.galleryStatus.textContent = this.gallery.dataset.loadedLabel;
      this.updateGalleryControls();
      return;
    }
    const previousIndex = this.currentGalleryIndex;
    const requestId = ++this.galleryRequestId;
    this.galleryTargetIndex = targetIndex;
    this.setActiveGalleryThumbnail(targetIndex, options.focusThumbnail);
    this.gallery.classList.add('is-loading');
    this.gallery.setAttribute('aria-busy', 'true');
    this.updateGalleryControls();
    if (this.galleryStatus) this.galleryStatus.textContent = this.gallery.dataset.loadingLabel;

    this.preloadGalleryImage(target).then(() => {
      if (requestId !== this.galleryRequestId) return;
      this.galleryMainImage.srcset = target.dataset.imageSrcset;
      this.galleryMainImage.src = target.dataset.imageSrc;
      this.galleryMainImage.alt = target.dataset.imageAlt;
      this.currentGalleryIndex = targetIndex;
      this.galleryTargetIndex = targetIndex;
      this.gallery.dataset.currentIndex = String(targetIndex);
      this.updateGalleryState(target, targetIndex);
      this.gallery.classList.remove('is-loading');
      this.gallery.setAttribute('aria-busy', 'false');
      if (this.galleryStatus) this.galleryStatus.textContent = this.gallery.dataset.loadedLabel;
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.galleryMainImage.animate([{ opacity: 0.55 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
      }
      this.preloadAdjacentImages(targetIndex);
    }).catch(() => {
      if (requestId !== this.galleryRequestId) return;
      this.galleryTargetIndex = previousIndex;
      this.setActiveGalleryThumbnail(previousIndex);
      this.gallery.classList.remove('is-loading');
      this.gallery.setAttribute('aria-busy', 'false');
      if (this.galleryStatus) this.galleryStatus.textContent = this.gallery.dataset.errorLabel;
    });
  }

  preloadGalleryImage(thumbnail) {
    const cacheKey = thumbnail.dataset.imageSrc;
    if (this.galleryImageCache.has(cacheKey)) return this.galleryImageCache.get(cacheKey);
    const request = new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      image.sizes = this.galleryMainImage.sizes || '(min-width: 981px) 52vw, 94vw';
      image.onload = resolve;
      image.onerror = reject;
      image.srcset = thumbnail.dataset.imageSrcset;
      image.src = thumbnail.dataset.imageSrc;
    });
    this.galleryImageCache.set(cacheKey, request);
    return request;
  }

  preloadAdjacentImages(index) {
    const schedule = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 250));
    schedule(() => {
      [index - 1, index + 1].forEach((adjacentIndex) => {
        const thumbnail = this.galleryThumbnails[adjacentIndex];
        if (thumbnail) this.preloadGalleryImage(thumbnail).catch(() => {});
      });
    });
  }

  setActiveGalleryThumbnail(index, focusThumbnail = false) {
    this.galleryThumbnails.forEach((thumbnail, thumbnailIndex) => {
      const active = thumbnailIndex === index;
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-pressed', String(active));
    });
    const activeThumbnail = this.galleryThumbnails[index];
    if (focusThumbnail) activeThumbnail?.focus();
    if (this.galleryRail && activeThumbnail) {
      const left = activeThumbnail.parentElement.offsetLeft - (this.galleryRail.clientWidth - activeThumbnail.offsetWidth) / 2;
      this.galleryRail.scrollTo({ left, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  updateGalleryState(thumbnail, index) {
    const imageNumber = index + 1;
    if (this.galleryCurrent) this.galleryCurrent.textContent = String(imageNumber);
    if (this.galleryZoomCurrent) this.galleryZoomCurrent.textContent = String(imageNumber);
    if (this.galleryMainButton) {
      this.galleryMainButton.setAttribute('aria-label', this.gallery.dataset.openLabelTemplate.replace('[number]', String(imageNumber)));
    }
    if (this.galleryZoomImage) {
      this.galleryZoomImage.removeAttribute('srcset');
      this.galleryZoomImage.src = thumbnail.dataset.imageZoomSrc;
      this.galleryZoomImage.alt = thumbnail.dataset.imageAlt;
    }
    this.updateGalleryControls();
  }

  updateGalleryControls() {
    if (this.galleryPrevious) this.galleryPrevious.disabled = this.galleryTargetIndex <= 0;
    if (this.galleryNext) this.galleryNext.disabled = this.galleryTargetIndex >= this.galleryThumbnails.length - 1;
  }

  handleGallerySwipe(event) {
    if (!this.galleryPointerStart) return;
    const deltaX = event.clientX - this.galleryPointerStart.x;
    const deltaY = event.clientY - this.galleryPointerStart.y;
    this.galleryPointerStart = null;
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    this.galleryDidSwipe = true;
    this.selectGalleryImage(this.galleryTargetIndex + (deltaX < 0 ? 1 : -1));
  }

  openGalleryDialog() {
    if (!this.galleryDialog?.showModal) return;
    this.galleryDialog.showModal();
  }

  initProductDetails() {
    this.querySelectorAll('[data-product-detail]').forEach((detail) => {
      const summary = detail.querySelector('summary');
      const content = detail.querySelector('[data-product-detail-content]');
      if (!summary || !content) return;
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleProductDetail(detail, content);
      });
    });
  }

  toggleProductDetail(detail, content) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const opening = !detail.open;
    content.getAnimations().forEach((animation) => animation.finish());
    if (reducedMotion) {
      detail.open = opening;
      return;
    }
    if (opening) {
      content.style.height = '0px';
      content.style.opacity = '0';
      detail.open = true;
      const targetHeight = content.scrollHeight;
      const animation = content.animate(
        [{ height: '0px', opacity: 0 }, { height: `${targetHeight}px`, opacity: 1 }],
        { duration: 280, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );
      animation.onfinish = () => {
        content.style.removeProperty('height');
        content.style.removeProperty('opacity');
      };
      return;
    }
    const startHeight = content.getBoundingClientRect().height;
    const animation = content.animate(
      [{ height: `${startHeight}px`, opacity: 1 }, { height: '0px', opacity: 0 }],
      { duration: 220, easing: 'cubic-bezier(0.4, 0, 1, 1)' }
    );
    animation.onfinish = () => {
      detail.open = false;
      content.style.removeProperty('height');
      content.style.removeProperty('opacity');
    };
  }
}

if (!customElements.get('rovynel-header')) customElements.define('rovynel-header', RovynelHeader);
if (!customElements.get('rovynel-product')) customElements.define('rovynel-product', RovynelProduct);
